import {
  getRateLimitRemainingSeconds,
  isRateLimitBlocked,
  setRateLimitCooldown,
} from './rateLimitCooldown';

export function parseRetryAfterSeconds(response?: {
  headers?: Record<string, unknown>;
  data?: unknown;
}): number {
  const headers = response?.headers || {};
  const headerValue =
    headers['retry-after'] ||
    headers['Retry-After'];

  if (headerValue != null && String(headerValue).trim() !== '') {
    const asInt = Number.parseInt(String(headerValue).trim(), 10);
    if (Number.isFinite(asInt) && asInt > 0) return asInt;

    const parsedDate = Date.parse(String(headerValue));
    if (!Number.isNaN(parsedDate)) {
      const diff = Math.ceil((parsedDate - Date.now()) / 1000);
      if (diff > 0) return diff;
    }
  }

  const body = response?.data;
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    const fromBody = record.retryAfterSeconds ?? record.retry_after_seconds;
    if (typeof fromBody === 'number' && fromBody > 0) return fromBody;
    if (typeof fromBody === 'string') {
      const parsed = Number.parseInt(fromBody, 10);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }

  return 60;
}

export function buildRateLimitMessage(retryAfterSeconds: number): string {
  const seconds = retryAfterSeconds > 0 ? retryAfterSeconds : 60;
  if (seconds <= 60) {
    return `Too many requests. Please wait ${seconds} second${seconds === 1 ? '' : 's'} and try again.`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `Too many requests. Please wait about ${minutes} minute${minutes === 1 ? '' : 's'} and try again.`;
}

export class RateLimitError extends Error {
  status: number;
  retryAfterSeconds: number;
  isRateLimit = true;

  constructor(message: string, retryAfterSeconds: number) {
    super(message);
    this.name = 'RateLimitError';
    this.status = 429;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export function handleRateLimitResponse(error: {
  response?: { headers?: Record<string, unknown>; data?: unknown };
}): RateLimitError {
  const retryAfterSeconds = parseRetryAfterSeconds(error.response);
  setRateLimitCooldown(retryAfterSeconds);
  const message = buildRateLimitMessage(retryAfterSeconds);
  return new RateLimitError(message, retryAfterSeconds);
}

export function assertNotRateLimited(): RateLimitError | null {
  if (!isRateLimitBlocked()) return null;
  const remaining = getRateLimitRemainingSeconds();
  return new RateLimitError(buildRateLimitMessage(remaining), remaining);
}
